/**
 * @fileoverview Bulk valuation / owed-estimate dialog.
 *
 * Shared across the Assets, Security Interests, and Creditors list panels: the
 * practitioner selects several rows, then applies ONE value (amount + optional
 * as-at date + note) to every selected row as an endorsed practitioner
 * valuation / owed estimate. Used for corrections such as zeroing out a batch
 * of speculative agent valuations.
 */

import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/ui/composed/overlays/dialog";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";

/** Parses a currency-ish string into a non-negative number, or null. */
function parseAmount(raw: string): number | null {
  const cleaned = raw.trim().replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

export interface BulkValuationInput {
  amount: number;
  asAtDate?: string;
  notes?: string;
}

export function BulkValuationDialog({
  open,
  onOpenChange,
  count,
  /** Dialog title, e.g. "Update valuations" or "Update owed estimates". */
  title,
  /** Noun used in the body copy, e.g. "asset" / "security interest" / "creditor". */
  itemNoun,
  /** Field label above the amount input, e.g. "Valuation ($)" / "Amount owed ($)". */
  amountLabel,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  title: string;
  itemNoun: string;
  amountLabel: string;
  submitting?: boolean;
  onSubmit: (input: BulkValuationInput) => void;
}) {
  const [amount, setAmount] = useState("");
  const [asAt, setAsAt] = useState("");
  const [notes, setNotes] = useState("");

  // Reset the form each time the dialog opens so a prior entry never lingers.
  useEffect(() => {
    if (open) {
      setAmount("");
      setAsAt("");
      setNotes("");
    }
  }, [open]);

  const parsed = parseAmount(amount);
  const pluralNoun = count === 1 ? itemNoun : `${itemNoun}s`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Applies this value to all {count} selected {pluralNoun} as an
            endorsed practitioner figure. This replaces the selected value on
            each row (previous figures stay in each row's history).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {amountLabel}
            </label>
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="h-9 text-sm tabular-nums"
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              As at
            </label>
            <DatePicker value={asAt} onChange={setAsAt} className="w-full" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Basis for this value..."
              rows={2}
              className="text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={parsed == null || submitting}
            onClick={() => {
              if (parsed == null) return;
              onSubmit({
                amount: parsed,
                asAtDate: asAt.trim() || undefined,
                notes: notes.trim() || undefined,
              });
            }}
          >
            {submitting ? "Applying..." : `Apply to ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
